import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import { BRONZE_TAGS } from "../../reducers/model/bgtFilters";
import { BronzeGroupTrackingApiResponse, BGTEligibilityResponse, BgtMiniProfileApi } from "./bronzeGroupTracking.types";

type BaseRequestParams = {
  aff: string;
  abo: string;
  period: string;
  targetAffAbo: string;
  locale: string;
};

type BgtMiniProfileApiRequestParams = {
  aff: string;
  abo: string;
  bonusPeriod: string;
  countryCode?: string;
};

type BgtEligibilityApiRequestParams = {
  affAbos: string[];
  period: string;
  countryCode: string;
};

type BGTOverviewPageRequestParams = {
  aff: string;
  abo: string;
  period: string;
  targetAffAbo: string;
  countryCode: string;
  locale: string;
};

export type BGTOverviewPageResponse = {
  overview: BronzeGroupTrackingApiResponse;
  eligibility: BGTEligibilityResponse;
};

export const bronzeGroupTrackingAPI = createApi({
  reducerPath: "bronzeGroupTrackingAPI",
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getBronzeGroupTrackingApi: builder.query<BronzeGroupTrackingApiResponse, BaseRequestParams>({
      query: (params) => {
        const { abo, aff, period, targetAffAbo, locale } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Period: period,
          TargetAffAbo: targetAffAbo,
          Locale: locale,
        });

        return `/acc-action-report-v2-api/api/bronze-group/overview?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getBgtMiniProfileApi: builder.query<BgtMiniProfileApi, BgtMiniProfileApiRequestParams>({
      query: (params) => {
        const { abo, aff, bonusPeriod, countryCode } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Period: bonusPeriod,
          IsoCountryCode: `${countryCode}`,
        });

        return `/acc-action-report-v2-api/api/bronze-group/mini-profile?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getBronzeGroupTrackingEligibilityApi: builder.query<BGTEligibilityResponse, BgtEligibilityApiRequestParams>({
      query: (params) => {
        const { affAbos, period, countryCode } = params;
        const queryParams = new URLSearchParams({
          AffAbos: `${affAbos}`,
          Period: period,
          IsoCountryCode: countryCode,
        });
        return `/acc-action-report-v2-api/api/bronze-group/eligibility?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getCombinedBronzeTrackingData: builder.query<BGTOverviewPageResponse, BGTOverviewPageRequestParams>({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const { aff, abo, period, targetAffAbo, countryCode, locale } = args;
        const trackingQueryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Period: period,
          TargetAffAbo: targetAffAbo,
          Locale: locale,
        });

        const overviewResult = await fetchWithBQ(
          `/acc-action-report-v2-api/api/bronze-group/overview?${trackingQueryParams}`,
        );

        if (overviewResult.error) {
          return { error: overviewResult.error };
        }

        const data = overviewResult.data as BronzeGroupTrackingApiResponse;

        const affAbos = data.awardProgress.trackingTilesData
          .filter((aboTile) => aboTile.eligibilityTag === BRONZE_TAGS.IN_WINDOW)
          .map((a) => `${a.affNumber}-${a.aboNumber}`);

        let eligibilityResult: { data: BGTEligibilityResponse } = {
          data: {
            AboBronzeEligibility: [],
            CallResults: [],
            Error: null,
          },
        };

        if (affAbos.length > 0) {
          const eligibilityQueryParams = new URLSearchParams({
            AffAbos: affAbos.join(","),
            Period: period,
            IsoCountryCode: countryCode,
          });

          const eligibilityFetch = await fetchWithBQ(
            `/acc-action-report-v2-api/api/bronze-group/eligibility?${eligibilityQueryParams}`,
          );

          if (eligibilityFetch.error) {
            return { error: eligibilityFetch.error };
          }

          eligibilityResult = { data: eligibilityFetch.data as BGTEligibilityResponse };
        }

        return {
          data: {
            overview: overviewResult.data as BronzeGroupTrackingApiResponse,
            eligibility: eligibilityResult.data,
          },
        };
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useGetBronzeGroupTrackingApiQuery,
  useGetBgtMiniProfileApiQuery,
  useGetBronzeGroupTrackingEligibilityApiQuery,
  useGetCombinedBronzeTrackingDataQuery,
} = bronzeGroupTrackingAPI;
